import React, { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import useFontLoadedListener from 'hooks/useFontLoadedListener';
import { isCaptionsEnabledSelector } from 'redux/modules/embed/selectors';
import { State } from 'redux/types';
import {
  createLayoutSegmentCaptionCuesSelector,
  layoutSegmentCaptionsStyleSelector,
} from '../../selectors';
import { useEditSceneModalStateContext } from '../../state';
import useEditSceneModal from '../../useEditSceneModal';
import { block } from '../../utils';
import useEditSceneCaptionsRenderer from '../useEditSceneCaptionsRenderer';
import useEditSceneCaptionsStyle from '../useEditSceneCaptionsStyle';
import useEditSceneCaptionsTimeSync from '../useEditSceneCaptionsTimeSync';
import EditSceneCaptionsLayerRnd from './EditSceneCaptionsLayerRnd';

/**
 * A layer in the preview canvas responsible for rendering captions
 */
const EditSceneCaptionsLayer: React.FC = () => {
  const wrapperNodeRef = useRef<HTMLDivElement | null>(null);

  const { videoId, segmentId } = useEditSceneModal();

  const {
    state: { captionsOverride, previewDimensions, slotCount },
  } = useEditSceneModalStateContext();

  const { isFontsLoaded } = useFontLoadedListener();

  const isCaptionsEnabled = useSelector(isCaptionsEnabledSelector);
  const captionCues = useSelector(
    createLayoutSegmentCaptionCuesSelector(videoId, segmentId),
  );

  const captionsStyle = useSelector(
    useMemo(
      () => (state: State) =>
        layoutSegmentCaptionsStyleSelector(state, slotCount, captionsOverride),
      [captionsOverride, slotCount],
    ),
  );

  const { captionsPositionStyle } = useEditSceneCaptionsStyle(
    captionsStyle,
    previewDimensions,
  );

  const { captionsRendererRef } = useEditSceneCaptionsRenderer({
    containerRef: wrapperNodeRef,
    captionCues,
    previewDimensions,
    isFontsLoaded,
  });

  useEditSceneCaptionsTimeSync({
    captionsRendererRef,
  });

  if (
    !isCaptionsEnabled ||
    !captionCues ||
    captionCues.length === 0 ||
    !previewDimensions ||
    !captionsPositionStyle
  ) {
    return null;
  }

  return (
    <EditSceneCaptionsLayerRnd captionsPositionStyle={captionsPositionStyle}>
      <div
        className={block('captions-layer')}
        ref={wrapperNodeRef}
        style={{
          ...captionsPositionStyle,
          // Rnd handles positioning, so reset these
          position: 'relative',
          left: 0,
          top: 0,
        }}
      />
    </EditSceneCaptionsLayerRnd>
  );
};

export default EditSceneCaptionsLayer;
